import { Button, DirectoryTable, Modal, PageHeader, useFormatters } from '@lumlab/platform-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganizationScope, useDirectory } from '@lumlab/platform-admin/web';
import type { Agency } from '../contract/index.ts';
import { AgencyForm } from './AgencyForm.tsx';

export function AdminAgenciesPage() {
    const { t } = useTranslation();
    return (
        <>
            <PageHeader
                title={t('tenders.administration.title')}
                description={t('tenders.administration.description')}
            />
            <OrganizationScope>
                {(organizationId) => <OrganizationAgencies organizationId={organizationId} />}
            </OrganizationScope>
        </>
    );
}

function OrganizationAgencies({ organizationId }: { organizationId: string }) {
    const { t } = useTranslation();
    const format = useFormatters();
    const path = `/api/admin/products/tenders/organizations/${organizationId}/agencies`;
    const agencies = useDirectory<Agency>(path);
    const [editing, setEditing] = useState<Agency | 'new' | null>(null);
    const close = () => setEditing(null);
    return (
        <>
            <Button onClick={() => setEditing('new')} className="self-end">
                {t('tenders.agencies.add')}
            </Button>
            <DirectoryTable
                items={agencies.data?.items ?? []}
                total={agencies.data?.total ?? 0}
                loading={agencies.isPending}
                error={agencies.error}
                search={agencies.search}
                onSearchChange={agencies.setSearch}
                page={agencies.page}
                pageSize={agencies.pageSize}
                onPageChange={agencies.setPage}
                columns={[
                    { key: 'name', label: t('tenders.agencies.name'), render: (agency) => agency.name },
                    { key: 'address', label: t('tenders.agencies.address'), render: (agency) => agency.address },
                    {
                        key: 'date',
                        label: t('platform.admin.createdAt'),
                        render: (agency) => format.date(new Date(agency.createdAt)),
                    },
                    {
                        key: 'actions',
                        label: t('platform.admin.actions'),
                        render: (agency) => (
                            <Button variant="light" size="xs" onClick={() => setEditing(agency)}>
                                {t('tenders.administration.edit')}
                            </Button>
                        ),
                    },
                ]}
            />
            <Modal
                opened={editing !== null}
                onClose={close}
                title={editing === 'new' ? t('tenders.agencies.add') : t('tenders.administration.edit')}
            >
                {editing && (
                    <AgencyForm
                        key={editing === 'new' ? 'new' : editing.id}
                        agency={editing === 'new' ? undefined : editing}
                        endpoint={path}
                        queryKey={['admin', path]}
                        onDone={close}
                    />
                )}
            </Modal>
        </>
    );
}
